/**
 * Shared automation hooks: stable kebab-case `id` values for the whole app.
 *
 * Conventions
 *  - Kebab-case strings, prefixed by area/page (signin-, signup-, dashboard-, navbar-, ...).
 *  - For repeated rows/cards, append a stable business id via helpers
 *    (e.g. tableRowTestId("tenants", id) -> "tenants-table-row-<id>").
 *  - Prefer `id` over the HTML `id` attribute. Only use `id` for
 *    singletons paired with a `<label htmlFor>` (see `singletonFormIds`).
 *  - Per-route page roots live in `./pageRootTestIds` (`page-<slug>`).
 */

export const automationTestIds = {
  // Day-0 onboarding
  onboardingStepper: "onboarding-stepper",
  onboardingStepHeading: "onboarding-step-heading",
  onboardingCategoryGrid: "onboarding-category-grid",
  onboardingCategoryContinue: "onboarding-category-continue",
  onboardingCategoryError: "onboarding-category-error",
  onboardingOrgNameInput: "onboarding-org-name",
  onboardingCountrySelect: "onboarding-country",
  onboardingDomainInput: "onboarding-domain",
  onboardingDomainStatus: "onboarding-domain-status",
  onboardingLogoUpload: "onboarding-logo-upload",
  onboardingLogoRemove: "onboarding-logo-remove",
  onboardingProfileBack: "onboarding-profile-back",
  onboardingProfileSave: "onboarding-profile-save",
  onboardingProfileFormError: "onboarding-profile-form-error",
  onboardingCertificatePreview: "onboarding-certificate-preview",
  onboardingWelcomeHeroCta: "onboarding-welcome-hero-cta",
  onboardingWelcomeHeroPreview: "onboarding-welcome-hero-preview",
  onboardingWelcomeViewAll: "onboarding-welcome-view-all",
  onboardingRecommendedRow: "onboarding-recommended-row",
  onboardingHelpCard: "onboarding-help-card",
  onboardingHelpTour: "onboarding-help-tour",
  onboardingHelpGuide: "onboarding-help-guide",

  cfLoader: "cf-loader",
  fullScreenLoader: "full-screen-loader",
  postLoginLoading: "post-login-loading",

  // Shared page error recovery
  pageErrorState: "page-error-state",
  pageErrorStateRetry: "page-error-state-retry",
  pageErrorStateSecondary: "page-error-state-secondary",

  // Dashboard (issuer)
  dashboardPage: "page-dashboard",
  dashboardTotalHolders: "dashboard-total-holders",
  dashboardTotalCredentials: "dashboard-total-credentials",
  dashboardTotalExpired: "dashboard-total-expired",
  dashboardTotalRevoked: "dashboard-total-revoked",
  dashboardEmptyState: "dashboard-empty-state",
  dashboardSeeTemplates: "dashboard-see-templates",
  dashboardChartBar: "chart-bar",
  dashboardChartPie: "chart-pie",
  dashboardCredentialsDetails: "dashboard-credentials-details",
  dashboardTopIssuers: "dashboard-top-issuers",
  dashboardRecentlyIssued: "dashboard-recently-issued",
  dashboardRecentlyIssuedTable: "dashboard-recently-issued-table",
  dashboardRecentlyIssuedViewAll: "dashboard-recently-issued-view-all",
  issuedCertificates: "issued-certificates",
  issuedCertificatesTable: "issued-certificates-table",
  issuedCertificatesBack: "issued-certificates-back",
  issuedCertificatesBreadcrumb: "issued-certificates-breadcrumb",
  dashboardErrorHandling: "dashboard-error-handling",

  topIssuers: "top-issuers",

  // Sign-in
  signinForm: "signin-form",
  signinEmailInput: "signin-email-input",
  signinPasswordInput: "signin-password-input",
  signinRememberMe: "signin-remember-me",
  signinForgotPassword: "signin-forgot-password",
  signinSubmit: "signin-submit",
  signinGoogle: "signin-google",
  signinMicrosoft: "signin-microsoft",
  signinSwitchToSignup: "signin-switch-to-signup",
  signinPasswordShow: "bi-show",
  signinPasswordHide: "bi-hide",

  // Sign-up
  signupModalHeader: "signup-modal-header",
  signupForm: "signup-form",
  signupFirstName: "signup-first-name",
  signupLastName: "signup-last-name",
  signupEmail: "signup-email",
  signupCountryCode: "signup-country-code",
  signupPhone: "signup-phone",
  signupPassword: "signup-password",
  signupConfirmPassword: "signup-confirm-password",
  signupSubmit: "signup-submit",
  signupGoogle: "signup-google",
  signupMicrosoft: "signup-microsoft",
  signupSwitchToSignin: "signup-switch-to-signin",
  signupSwitchToSigninAfterSuccess: "signup-switch-to-signin-after-success",
  signupSuccessPanel: "signup-success-message",
  signupPasswordShow: "bi-show-password",
  signupPasswordHide: "bi-hide-password",
  signupConfirmPasswordShow: "signup-confirm-password-show",
  signupConfirmPasswordHide: "signup-confirm-password-hide",
  authBrandHeader: "auth-brand-header",
  authBrandHeaderAccount: "auth-brand-header-account",
  authBrandHeaderOrgName: "auth-brand-header-org-name",

  // Forgot password
  forgotPasswordPage: "forgot-password-page",
  forgotPasswordCard: "forgot-password-card",
  forgotPasswordForm: "forgot-password-form",
  forgotPasswordEmailInput: "forgot-password-email-input",
  forgotPasswordSendOtp: "forgot-password-send-otp",
  forgotPasswordOtpInput: "forgot-password-otp-input",
  forgotPasswordVerifyOtp: "forgot-password-verify-otp",
  forgotPasswordNewPassword: "forgot-password-new-password",
  forgotPasswordConfirm: "forgot-password-confirm-password",
  forgotPasswordSubmit: "forgot-password-submit",
  forgotPasswordSignIn: "forgot-password-sign-in",
  forgotPasswordToggleNewVisibility: "forgot-password-toggle-new-visibility",
  forgotPasswordToggleConfirmVisibility:
    "forgot-password-toggle-confirm-visibility",
  forgotPasswordResendOtp: "forgot-password-resend-otp",
  forgotPasswordError: "forgot-password-error",
  forgotPasswordSuccess: "forgot-password-success",

  // Reset password (logged-in)
  resetPasswordCurrent: "reset-password-current",
  resetPasswordNew: "reset-password-new",
  resetPasswordConfirm: "reset-password-confirm",
  resetPasswordSubmit: "reset-password-submit",
  resetPasswordToggleCurrent: "reset-password-toggle-current",
  resetPasswordToggleNew: "reset-password-toggle-new",
  resetPasswordToggleConfirm: "reset-password-toggle-confirm",
  resetPasswordFirstTimeNote: "reset-password-first-time-note",
  passwordUpdatedModal: "password-updated-modal",
  passwordUpdatedModalPanel: "password-updated-modal-panel",
  passwordUpdatedModalTitle: "password-updated-modal-title",
  passwordUpdatedModalMessage: "password-updated-modal-message",
  passwordUpdatedModalAccount: "password-updated-modal-account",
  passwordUpdatedModalSignIn: "password-updated-modal-sign-in",
  passwordUpdatedModalClose: "password-updated-modal-close",

  // Activate account
  activateAccountPassword: "activate-account-password",
  activateAccountConfirmPassword: "activate-account-confirm-password",
  activateAccountSubmit: "activate-account-submit",
  activateAccountTogglePassword: "activate-account-toggle-password",
  activateAccountToggleConfirm: "activate-account-toggle-confirm",
  activateAccountError: "activate-account-error",
  activateAccountSuccess: "activate-account-success",

  // Navbar / shell
  navbar: "navbar-shell",
  navbarLanguagesIcon: "languages-icon",
  navbarOrgLink: "navbar-org-home",
  navbarOrgNameFallback: "navbar-org-name-fallback",
  navbarOrgHomeFallback: "navbar-org-home-fallback",
  navbarUserMenuButton: "navbar-user-menu-button",
  navbarUserMenuItems: "navbar-user-menu-items",
  navbarLanguageMenuButton: "navbar-language-menu-button",
  navbarLanguageMenuItems: "navbar-language-menu-items",
  navbarLanguageSelect: "navbar-language-select",
  navbarSignOut: "navbar-sign-out",
  navbarSignInTrigger: "navbar-sign-in-trigger",
  navbarSignUpTrigger: "navbar-sign-up-trigger",
  navbarProfileLink: "navbar-profile-link",
  navbarSettingsLink: "navbar-settings-link",
  navbarResetPasswordLink: "navbar-reset-password-link",
  navbarHomeLink: "navbar-home-link",
  navbarBackButton: "navbar-back-button",

  // Sidebar
  sidebar: "sidebar",
  sidebarToggle: "sidebar-toggle",

  /** Default root prefix when callers omit `testId` on `DateFilterDropdown`. */
  dateFilterDropdownDefault: "date-filter-dropdown",

  // Login modal (headless Dialog)
  loginModal: "login-modal",
  loginModalBackdrop: "login-modal-backdrop",
  loginModalPanel: "login-modal-panel",
  loginModalClose: "login-modal-close",

  // Account not active (nested login modal)
  accountNotActiveModal: "account-not-active-modal",
  accountNotActiveModalBackdrop: "account-not-active-modal-backdrop",
  accountNotActiveModalPanel: "account-not-active-modal-panel",
  accountNotActiveModalClose: "account-not-active-modal-close",
  accountNotActiveModalTitle: "account-not-active-modal-title",
  accountNotActiveModalMessage: "account-not-active-modal-message",
  accountNotActiveModalOk: "account-not-active-modal-ok",

  // Breadcrumb
  breadcrumb: "breadcrumb",

  // Loaders / pagination
  smallCfLoader: "small-cf-loader",
  dataTable: "cf-data-table",
  dataTablePrev: "cf-data-table-prev",
  dataTableNext: "cf-data-table-next",
  dataTableEmpty: "cf-data-table-empty",
  dataTableSelectAll: "cf-data-table-select-all",
  layoutBaseMain: "layout-base-main",
  layoutAuthMain: "layout-auth-main",
  layoutNavbarWrapper: "layout-navbar-wrapper",
  layoutSidebarAside: "layout-sidebar-aside",

  // Common modals
  modalRoot: "modal",
  modalClose: "modal-close",
  modalConfirm: "modal-confirm",
  modalCancel: "modal-cancel",
  modalBackdrop: "modal-backdrop",

  // Common alerts/toasts
  toast: "toast",
  toastClose: "toast-close",

  // Tabs
  tabsRoot: "tabs",

  // Generic action icons
  iconView: "icon-view",
  iconEdit: "icon-edit",
  iconDelete: "icon-delete",
  iconDownload: "icon-download",
  iconUpload: "icon-upload",
  iconCopy: "icon-copy",
  iconRefresh: "icon-refresh",
  iconClose: "icon-close",
  iconAdd: "icon-add",
  iconFilter: "icon-filter",
  iconSearch: "icon-search",
};

/** HTML id attributes — at most one per document fragment; pair with labels where applicable. */
export const singletonFormIds = {
  signinForm: "signin-form",
  signupForm: "signup-form",
  forgotPasswordForm: "forgot-password-form",
  resetPasswordForm: "reset-password-form",
  activateAccountForm: "activate-account-form",
};

export function dashboardErrorTestId(errorMessage) {
  return `error-${String(errorMessage).replace(/\s+/g, "-")}`;
}

const slug = (value) =>
  String(value ?? "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

/** Generate a stable testid for an interactive element on a page. */
export function elementTestId(page, kind, name, suffix) {
  const parts = [slug(page), slug(kind), slug(name)];
  if (suffix !== undefined && suffix !== null && suffix !== "") {
    parts.push(slug(suffix));
  }
  return parts.filter(Boolean).join("-");
}

/** Helpers shaped by the element type. */
export const buttonTestId = (page, name, suffix) =>
  elementTestId(page, "btn", name, suffix);
export const inputTestId = (page, name, suffix) =>
  elementTestId(page, "input", name, suffix);
export const dropdownTestId = (page, name, suffix) =>
  elementTestId(page, "dropdown", name, suffix);
export const dropdownOptionTestId = (page, name, value) =>
  elementTestId(page, "dropdown-option", name, value);
export const tabTestId = (page, name) => elementTestId(page, "tab", name);
export const iconTestId = (page, name, suffix) =>
  elementTestId(page, "icon", name, suffix);
export const checkboxTestId = (page, name, suffix) =>
  elementTestId(page, "checkbox", name, suffix);
export const radioTestId = (page, name, suffix) =>
  elementTestId(page, "radio", name, suffix);
export const linkTestId = (page, name, suffix) =>
  elementTestId(page, "link", name, suffix);
export const toggleTestId = (page, name, suffix) =>
  elementTestId(page, "toggle", name, suffix);
export const alertTestId = (page, tone, name) =>
  elementTestId(page, `alert-${slug(tone)}`, name);
export const errorTestId = (page, name) => alertTestId(page, "error", name);
export const successTestId = (page, name) => alertTestId(page, "success", name);
export const warningTestId = (page, name) => alertTestId(page, "warning", name);
export const infoTestId = (page, name) => alertTestId(page, "info", name);
export const tableTestId = (page, name) =>
  elementTestId(page, "table", name || "main");
export const tableRowTestId = (page, rowKey, suffix) =>
  elementTestId(page, "table-row", rowKey, suffix);
export const tableHeaderTestId = (page, key) =>
  elementTestId(page, "table-header", key);
export const tableCellTestId = (page, rowKey, columnKey) =>
  elementTestId(page, "table-cell", `${slug(rowKey)}-${slug(columnKey)}`);
export const cardTestId = (page, name, suffix) =>
  elementTestId(page, "card", name, suffix);
export const modalTestId = (page, name) =>
  elementTestId(page, "modal", name || "root");
